import React, { useEffect, useMemo, useState } from "react";
import SubTitle from "../../../components/common/SubTitle";
import { CampaignButtonVariant } from "../../../components/common/Button/Button";
import CampaignCard from "../../../components/common/CampaignCard/CampaignCard";
import CampaignCardSkeleton from "../../../components/common/CampaignCard/CampaignCardSkeleton";
import { Box, Typography, useTheme } from "@mui/material";
import { useMediaQuery } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Images } from "../../../utils/Images";
import { useDispatch, useSelector } from "react-redux";
import { fetchCmsHomeAction } from "../../../store/slices/cmsSlice";
import { getCampaigns } from "../../../api/services/campaignService";
import { canViewPrivateCampaign } from "../../../utils/authUtils";
import { matchesCategory } from "../../../utils/helper";

const UrgentNeedsCampaign = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isAbove1200 = useMediaQuery("(min-width: 1200px)");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cmsHome = useSelector((state) => state.cms.home);
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!cmsHome) {
      dispatch(fetchCmsHomeAction());
    }
  }, [cmsHome, dispatch]);

  useEffect(() => {
    let isMounted = true;
    const fetchUrgentCampaigns = async () => {
      try {
        setLoading(true);
        const response = await getCampaigns();
        if (!isMounted) return;
        const allCampaigns = response?.data || [];
        const privacyFiltered = allCampaigns.filter((c) => {
          if (c.IsPrivate === 0 || !c.IsPrivate) return true;
          if (c.IsPrivate === 1) {
            return canViewPrivateCampaign(user, isAuthenticated);
          }
          return true;
        });
        const urgent = privacyFiltered.filter((campaign) =>
          matchesCategory(campaign.category, "Urgent")
        );
        const sorted = [...urgent].sort((a, b) => {
          const dateA = new Date(a.updatedAt || a.createdAt || 0);
          const dateB = new Date(b.updatedAt || b.createdAt || 0);
          return dateB - dateA;
        });
        setCampaigns(sorted.slice(0, 3));
      } catch (e) {
        console.error("Error fetching urgent campaigns (profile slider):", e);
        if (isMounted) setCampaigns([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchUrgentCampaigns();
    return () => {
      isMounted = false;
    };
  }, [user, isAuthenticated]);

  const descriptionText = useMemo(() => {
    const sections = Array.isArray(cmsHome?.data) ? cmsHome.data : [];
    // Use Normal Campaign description (id: 104), same as urgent campaign section
    const section = sections.find((s) => s?.id === 104);
    return (
      section?.description ||
      ""
    );
  }, [cmsHome]);
  return (
    <div
      style={{
        width: isMobile ? "100%" : "100%",
        // minHeight: "855px", // Changed from fixed height to minHeight
        borderRadius: isMobile ? "16px" : "36px",
        backgroundColor: "rgba(232, 232, 232, 0.36)",
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-evenly",
        alignItems: "center",
        marginTop: "40px",
        marginBottom: "40px",
        padding: isMobile ? "26px 0px 20px 0px" : "60px 0px 40px 0px", // Responsive padding - removed left/right padding
        position: "relative", // Added for absolute positioning of decorative elements
        overflow: "hidden", // Added to contain the decorative elements
      }}
    >
      {/* Decorative Orange Sparks - Top Left (Desktop Only) */}
      <img
        src={Images.orangeSparks}
        alt="Orange Sparks"
        style={{
          position: "absolute",
          top: isMobile ? "10px" : isAbove1200 ? "61px" : "20px",
          left: isMobile ? "10px" : isAbove1200 ? "116px" : "20px",
          width: isMobile ? "40px" : isAbove1200 ? "50px" : "45px",
          height: "auto",
          zIndex: 1,
          display: isMobile ? "none" : "block", // Hide on mobile
        }}
      />

      {/* Decorative Images.heart - Top Right (Desktop Only) */}
      <img
        src={Images.heart}
        alt="Images.heart"
        style={{
          position: "absolute",
          top: isMobile ? "15px" : isAbove1200 ? "116px" : "80px",
          right: isMobile ? "15px" : isAbove1200 ? "275px" : "30px",
          width: isMobile ? "30px" : isAbove1200 ? "22px" : "25px",
          height: "auto",
          zIndex: 1,
          display: isMobile ? "none" : "block", // Hide on mobile
        }}
      />

      {/* Decorative String Hand - Mobile Only */}
      <img
        src={Images.stringHand}
        alt="String Hand"
        style={{
          position: "absolute",
          bottom: "16px",
          left: "0px",
          width: "53px",
          height: "auto",
          zIndex: 1,
          display: isMobile ? "block" : "none", // Show only on mobile
          transform: "scaleX(-1)", // Invert horizontally
        }}
      />

      {/* Title Box */}
      {/* <div
        style={{
          marginBottom: isMobile ? "8px" : "16px", // Responsive margin bottom
          position: "relative",
          zIndex: 2,
        }}
      >
        <Title isMobile={isMobile}>Featured Campaigns</Title>
      </div> */}

      {/* Subtitle Box */}
      {/* <div
        style={{
          width: "100%",
          maxWidth: isMobile ? "280px" : "506px", // Responsive max width
          marginBottom: isMobile ? "20px" : "38px", // Responsive margin bottom
          position: "relative",
          zIndex: 2,
        }}
      >
        <SubTitle isMobile={isMobile}>
          Urgent Campaigns That Need Your Help Now
        </SubTitle>
      </div> */}

      {/* Title */}
      <Box
        sx={{ mb: isMobile ? "8px" : "16px", zIndex: 2, position: "relative" }}
      >
        <SubTitle isMobile={isMobile}>Urgent  Campaign</SubTitle>
      </Box>

      {/* Red Hearts - Separate from title, mobile only */}
      <Box
        component="img"
        src={Images.redHearts}
        alt="Red Hearts"
        sx={{
          position: "absolute",
          top: isMobile ? "24px" : "50px",
          right: isMobile ? "86px" : "40px",
          width: isMobile ? "30px" : "28px",
          height: isMobile ? "30px" : "28px",
          display: isMobile ? "block" : "none", // Show only on mobile
          zIndex: 2,
        }}
      />

      {/* Subtitle */}
      <Box
        sx={{
          width: "100%",
          mb: isMobile ? "20px" : "38px",
          fontSize: "14px",
        }}
      >
        <Typography
          isMobile={isMobile}
          sx={{
            fontSize: isMobile ? "10px" : "14px",
            color: "#666666",
            textAlign: "center",
            fontWeight: 400,
            paddingInline: "10px",
            letterSpacing: "-0.2px",
          }}
        >
          {descriptionText}
        </Typography>
      </Box>

      {/* Campaign Cards Section */}
      <div
        style={{
          display: "flex",
          width: "100%",
          justifyContent: "space-evenly",
          alignItems: "center",
          gap: isMobile ? "16px" : "24px",
          marginBottom: isMobile ? "20px" : "48px",
          paddingLeft: isMobile ? "16px" : "34px", // Responsive left padding
          paddingRight: isMobile ? "16px" : "34px", // Responsive right padding
          overflowX: "auto",
          overflowY: "hidden",
          //   paddingBottom: "10px", // Add some padding to account for scrollbar
          scrollbarWidth: "none", // Hide scrollbar in Firefox
          msOverflowStyle: "none", // Hide scrollbar in IE/Edge
        }}
        className="hide-scrollbar" // We'll add CSS for this class
      >
        {loading ? (
          Array.from({ length: 3 }).map((_, index) => (
            <CampaignCardSkeleton key={`urgent-slider-skel-${index}`} />
          ))
        ) : campaigns.length > 0 ? (
          campaigns.map((campaign) => (
            <CampaignCard key={campaign.id} campaignData={campaign} />
          ))
        ) : (
          <Typography
            sx={{
              color: "#666666",
              textAlign: "center",
              fontSize: "14px",
              padding: "20px",
              width: "100%",
            }}
          >
            No urgent campaigns available at the moment.
          </Typography>
        )}
      </div>

      {/* Button Box */}
      <Box sx={{ mt: isMobile ? "30px" : 0 }}>
        <CampaignButtonVariant
          text="View All"
          isMobile={isMobile}
          onClick={() => navigate("/campaigns")}
        />
      </Box>
    </div>
  );
};

export default UrgentNeedsCampaign;
